const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const reverseGeocodeCache = new Map();

export const cleanLocationPart = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const normalized = text.toLowerCase();
  if (normalized === "unknown" || normalized === "unknown city" || normalized === "unknown state") return "";
  return text;
};

export const getLocationCacheKey = (latitude, longitude, precision = 2) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
  return `${lat.toFixed(precision)},${lng.toFixed(precision)}`;
};

export const extractLocationPartsFromMapboxResponse = (payload) => {
  const features = Array.isArray(payload?.features) ? payload.features : [];
  let city = "";
  let state = "";

  const readFeature = (feature) => {
    if (!feature) return;

    const placeTypes = Array.isArray(feature.place_type) ? feature.place_type : [];
    const text = cleanLocationPart(feature.text || feature.place_name);

    if (!city && ["place", "locality", "district", "neighborhood"].some((type) => placeTypes.includes(type))) {
      city = text;
    }

    if (!state && placeTypes.includes("region")) {
      state = cleanLocationPart(feature.text) || cleanLocationPart(feature.properties?.short_code?.split("-").pop()?.toUpperCase());
    }

    const context = Array.isArray(feature.context) ? feature.context : [];
    for (const item of context) {
      const itemId = String(item?.id || "");
      if (!city && ["place.", "locality.", "district.", "neighborhood."].some((prefix) => itemId.startsWith(prefix))) {
        city = cleanLocationPart(item.text);
      }
      if (!state && itemId.startsWith("region.")) {
        state = cleanLocationPart(item.text) || cleanLocationPart(item.short_code?.split("-").pop()?.toUpperCase());
      }
    }
  };

  for (const feature of features) {
    readFeature(feature);
    if (city && state) break;
  }

  if (!city && features[0]) {
    city = cleanLocationPart(features[0].text || features[0].place_name);
  }

  const locationLabel = [city, state].filter(Boolean).join(", ");
  return { city, state, locationLabel };
};

export const reverseGeocodeCoordinates = async (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!MAPBOX_TOKEN || !Number.isFinite(lat) || !Number.isFinite(lng)) return {};

  const cacheKey = getLocationCacheKey(lat, lng);
  if (cacheKey && reverseGeocodeCache.has(cacheKey)) {
    return reverseGeocodeCache.get(cacheKey);
  }

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=place,locality,district,neighborhood,region&limit=5&access_token=${MAPBOX_TOKEN}`,
    );
    if (!response.ok) return {};
    const payload = await response.json();
    const resolved = extractLocationPartsFromMapboxResponse(payload);
    if (cacheKey) reverseGeocodeCache.set(cacheKey, resolved);
    return resolved;
  } catch (_) {
    return {};
  }
};

export const buildLocationTelemetry = async (payload) => {
  const latitude = Number(payload?.latitude ?? payload?.lat);
  const longitude = Number(payload?.longitude ?? payload?.lng);

  const nextPayload = {
    ...payload,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
  };

  const existingCity = cleanLocationPart(payload?.city);
  const existingState = cleanLocationPart(payload?.state);
  const existingLabel = cleanLocationPart(payload?.location_label);

  let resolved = {};
  if (!existingCity || !existingState || !existingLabel) {
    resolved = await reverseGeocodeCoordinates(latitude, longitude);
  }

  const city = existingCity || cleanLocationPart(resolved.city);
  const state = existingState || cleanLocationPart(resolved.state);
  const locationLabel = existingLabel || cleanLocationPart(resolved.locationLabel) || [city, state].filter(Boolean).join(", ");

  if (city) nextPayload.city = city;
  if (state) nextPayload.state = state;
  if (locationLabel) nextPayload.location_label = locationLabel;

  return nextPayload;
};
